// Modelled directly on Hedge-Fund-Tracking-System AumAreaChart:
// gradient fill, dashed horizontal grid only, theme-aware colors,
// custom tooltip on bg-surface.
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../layout/theme-provider';
import { useSnapshots } from '../../hooks/useApi';
import { formatTime } from '../../lib/utils';

interface Props {
  driftThresholdBps: number;
  height?: number;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: number;
}) {
  if (!active || !payload?.length || label === undefined) return null;
  return (
    <div className="rounded-lg border border-border bg-surface shadow-lg p-3 text-sm">
      <p className="text-muted-foreground mb-1">{formatTime(label)}</p>
      <p className="font-semibold text-foreground tabular-nums">
        Drift: {payload[0]!.value} bps
      </p>
    </div>
  );
}

export function DriftAreaChart({ driftThresholdBps, height = 240 }: Props) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const gridColor = isDark ? 'hsl(215 25% 27%)' : 'hsl(214 32% 91%)';
  const textColor = isDark ? 'hsl(215 20% 65%)' : 'hsl(215 16% 47%)';
  const areaColor = 'hsl(221 83% 53%)';

  const { data: snapshots } = useSnapshots(24);
  const formatted = (snapshots ?? []).map((s) => ({
    ts: s.ts,
    drift: s.driftBps,
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={formatted} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="driftGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={areaColor} stopOpacity={0.15} />
            <stop offset="95%" stopColor={areaColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
        <XAxis
          dataKey="ts"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={formatTime}
          tick={{ fill: textColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          minTickGap={48}
        />
        <YAxis
          tick={{ fill: textColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={52}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: gridColor }} />
        <ReferenceLine
          y={driftThresholdBps}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <ReferenceLine
          y={-driftThresholdBps}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <Area
          type="monotone"
          dataKey="drift"
          stroke={areaColor}
          strokeWidth={2}
          fill="url(#driftGradient)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
